/**
 * Stable, privacy-respectful device fingerprint used as one signal
 * (NOT the only one) for signup-trial abuse detection.
 *
 * Industry pattern: hash of stable UA + screen + timezone + language.
 * Persisted in localStorage so legitimate re-installs of the web app
 * don't burn the user's one-trial allowance from the same browser.
 */

const STORAGE_KEY = 'elefa.device.fp.v1';

async function sha256Hex(input: string): Promise<string> {
  try {
    const buf = new TextEncoder().encode(input);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  } catch {
    // Fallback: not cryptographic, but stable enough as a soft signal.
    let h = 0;
    for (let i = 0; i < input.length; i++) {
      h = (h << 5) - h + input.charCodeAt(i);
      h |= 0;
    }
    return `fb_${(h >>> 0).toString(16)}`;
  }
}

export async function getDeviceFingerprint(): Promise<string> {
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached && cached.length > 16) return cached;
  } catch {
    // localStorage unavailable (private mode) — recompute every time
  }

  const parts = [
    navigator.userAgent || '',
    navigator.language || '',
    String(screen.width || 0),
    String(screen.height || 0),
    String(screen.colorDepth || 0),
    String(new Date().getTimezoneOffset()),
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    String((navigator as Navigator & { hardwareConcurrency?: number }).hardwareConcurrency || 0),
    String((navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0),
  ].join('|');

  const fp = await sha256Hex(parts);
  try {
    localStorage.setItem(STORAGE_KEY, fp);
  } catch {
    // ignore
  }
  return fp;
}
